import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useAuthUser, useLogout } from "#/api/auth";
import { useBillingStatus, usePortal } from "#/api/billing";
import { UpgradeModal } from "#/components/UpgradeModal";
import { Menu, X } from "lucide-react";

export function AuthNav() {
  const { data: user, isPending, isFetching } = useAuthUser();
  const { data: billing } = useBillingStatus(!!user);
  const logout = useLogout();
  const portal = usePortal();
  const isLoadingAuth = isPending || (isFetching && !user);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <nav className="fixed left-0 right-0 top-0 z-60 flex items-center justify-between px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] md:px-6 md:py-5 md:pb-5 md:pt-5">
      <Link
        to="/"
        className="flex items-center gap-2.5 text-foreground no-underline"
      >
        <div className="h-5 w-5 rounded-full border border-primary/60 shadow-[0_0_18px_rgba(180,160,220,0.25)]" />
        <span className="font-serif text-lg font-light tracking-wide">
          Ineya
        </span>
      </Link>

      {/* Desktop nav */}
      <div className="hidden items-center gap-3 md:flex">
        {isLoadingAuth ? (
          <div className="h-8 w-16 rounded-full bg-white/5" />
        ) : user ? (
          <>
            <Link
              to="/beliefs"
              className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs font-medium text-muted-foreground backdrop-blur-sm transition-all hover:border-white/20 hover:text-foreground"
            >
              Your beliefs
            </Link>
            {billing?.plan === "premium" ? (
              <button
                onClick={() => portal.mutate()}
                disabled={portal.isPending}
                className="rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-xs font-medium text-primary backdrop-blur-sm transition-all hover:border-primary/50 disabled:opacity-50"
              >
                {portal.isPending ? "Opening..." : "Manage plan"}
              </button>
            ) : billing?.plan === "free" ? (
              <UpgradeModal>
                <button className="rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-xs font-medium text-primary backdrop-blur-sm transition-all hover:border-primary/50 disabled:opacity-50">
                  Go Premium
                </button>
              </UpgradeModal>
            ) : null}
            {user.picture && (
              <img
                src={user.picture}
                alt={user.name || user.email}
                className="h-7 w-7 rounded-full border border-white/10 object-cover"
              />
            )}
            <button
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs font-medium text-muted-foreground backdrop-blur-sm transition-all hover:border-white/20 hover:text-foreground disabled:opacity-50"
            >
              {logout.isPending ? "Signing out..." : "Sign out"}
            </button>
          </>
        ) : (
          <Link
            to="/login"
            className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs font-medium text-muted-foreground backdrop-blur-sm transition-all hover:border-white/20 hover:text-foreground"
          >
            Sign in
          </Link>
        )}
      </div>

      {/* Mobile hamburger */}
      <button
        className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-muted-foreground backdrop-blur-sm transition-all hover:border-white/20 hover:text-foreground md:hidden"
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Toggle menu"
      >
        {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
      </button>

      {/* Mobile menu dropdown */}
      {menuOpen && (
        <div className="absolute right-4 top-[calc(env(safe-area-inset-top,0px)+3.25rem)] z-50 w-48 rounded-2xl border border-white/10 bg-background/95 p-3 shadow-2xl backdrop-blur-xl md:hidden">
          {isLoadingAuth ? (
            <div className="h-8 w-full rounded-full bg-white/5" />
          ) : user ? (
            <div className="flex flex-col gap-2">
              <Link
                to="/beliefs"
                className="rounded-xl px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground"
                onClick={() => setMenuOpen(false)}
              >
                Your beliefs
              </Link>
              {billing?.plan === "premium" ? (
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    portal.mutate();
                  }}
                  disabled={portal.isPending}
                  className="rounded-xl px-3 py-2 text-left text-sm text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
                >
                  {portal.isPending ? "Opening..." : "Manage plan"}
                </button>
              ) : billing?.plan === "free" ? (
                <UpgradeModal>
                  <button
                    className="w-full rounded-xl px-3 py-2 text-left text-sm text-primary transition-colors hover:bg-primary/10"
                    onClick={() => setMenuOpen(false)}
                  >
                    Go Premium
                  </button>
                </UpgradeModal>
              ) : null}
              <div className="my-1 h-px bg-white/10" />
              <button
                onClick={() => {
                  setMenuOpen(false);
                  logout.mutate();
                }}
                disabled={logout.isPending}
                className="rounded-xl px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground disabled:opacity-50"
              >
                {logout.isPending ? "Signing out..." : "Sign out"}
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              className="block rounded-xl px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground"
              onClick={() => setMenuOpen(false)}
            >
              Sign in
            </Link>
          )}
        </div>
      )}
    </nav>
  );
}
